/**
 * The `contentSx` every DESKTOP filter side panel passes to `DrawerPopup`.
 *
 * ## The bug this fixes
 *
 * The four filter drawers each render a flex column — a scrolling body, then the
 * footer — and each states `height: '100%'` on that column so the footer's
 * `marginTop: auto` has free space to push into. That `100%` resolved to `auto`,
 * so it never did.
 *
 * The chain, top down. The desktop panels give the Paper a definite height
 * (`calc(100% - 32px)`), and `DrawerPopup`'s outer content Box correctly takes
 * `height: '100%'` of it for a non-bottom anchor. But the INNER Box that actually
 * renders `child` is `flex: fullScreen ? 1 : 'unset'` — and these panels are not
 * `fullScreen` (deliberately: `fullScreen` forces `100vh` + `overflow: hidden` on
 * the Paper). So that Box sized to its content, and a percentage height resolved
 * against a content-sized parent is `auto` per CSS. The child column was therefore
 * exactly as tall as its filters, `marginTop: auto` had zero free space to
 * distribute, and Reset/Apply landed wherever the last filter happened to end —
 * mid-panel, with the rest of the viewport-height panel empty beneath them.
 *
 * `flex: 1` makes that Box take the height its parent already has; `minHeight: 0`
 * lets it be SHRUNK to that height rather than to its content (a flex item's
 * default `min-height: auto` is what breaks every nested scroll chain, and is the
 * same fix `DrawerPopup` already applies for `fullScreen`); the flex column
 * declaration passes the definite height down one more level to the child.
 *
 * ## Why here and not in `DrawerPopup`
 *
 * `DrawerPopup` is the app's universal sheet — legal documents, pickers, confirms,
 * every bottom sheet — and making its content Box fill by default would change the
 * layout of every one of them. The panels that need it are the ones whose Paper has
 * a stated height AND whose child is a footer-pinned flex column, which is exactly
 * this set. Scoping it to them is the smaller claim.
 *
 * ## Desktop only
 *
 * On a phone these same drawers are content-height bottom sheets (`height: auto`,
 * `maxHeight: 85vh`), where the footer follows the content and SHOULD — a sheet
 * that pinned its buttons to a 85vh floor would open with a band of empty space
 * above them. Pass this only on the `!isMobile` branch.
 */
export const FILTER_PANEL_CONTENT_SX = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
} as const
